import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link, NavLink, useLangNavigate, stripLangPrefix, applyLangPrefix } from "@/components/LangLink";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLoginModal, useAuth } from "@/App";
import { SERVICES, MENU_COLUMNS } from "@/data/site";
import { LANGUAGES, LANG_CODES } from "@/i18n";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Our Services" },
  { to: "/membership", label: "Membership" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact Us" },
];

const navClass = ({ isActive }) =>
  `relative whitespace-nowrap text-[0.62rem] 2xl:text-[0.68rem] uppercase tracking-[0.22em] 2xl:tracking-[0.28em] transition-colors duration-300 ${
    isActive ? "text-gold" : "text-white/70 hover:text-white"
  } after:absolute after:-bottom-2 after:left-0 after:h-px after:bg-gold after:transition-[width] after:duration-500 ${
    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
  }`;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = Object.keys(LANG_CODES).find((k) => LANG_CODES[k] === (i18n.language || "en").split("-")[0]) || "EN";
  const [updatesAvailable, setUpdatesAvailable] = useState(false);
  const openLogin = useLoginModal();
  const { user } = useAuth();
  const navigate = useNavigate();
  const langNavigate = useLangNavigate();
  const location = useLocation();

  // Switch language and reflect it in the URL (/pt, /es, ... — English stays unprefixed)
  const setLang = (l) => {
    const code = LANG_CODES[l];
    const base = stripLangPrefix(location.pathname) + (location.hash || "");
    i18n.changeLanguage(code);
    navigate(applyLangPrefix(base, code === "en" ? "" : `/${code}`));
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("dc_token");
    if (!user || !token) {
      setUpdatesAvailable(false);
      return;
    }
    const check = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/updates-flag`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => setUpdatesAvailable(Boolean(d?.updates_available)))
        .catch(() => {});
    };
    check();
    const interval = setInterval(check, 90000);
    const onFocus = () => check();
    const onChanged = () => check();
    window.addEventListener("focus", onFocus);
    window.addEventListener("dc-updates-changed", onChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("dc-updates-changed", onChanged);
    };
  }, [user]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 bg-black/80 backdrop-blur-xl border-b transition-colors duration-500 ${
          scrolled ? "border-white/10" : "border-transparent"
        }`}
      >
        <div className="max-w-[110rem] mx-auto pl-4 pr-6 lg:pl-6 lg:pr-8 2xl:pr-12 h-24 flex items-center justify-between gap-6">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-4 group flex-shrink-0">
            <img src="/logo-gold.png" alt="Delux Concierge" className="h-[4.6rem] w-auto transition-transform duration-500 group-hover:scale-105" />
            <span className="hidden 2xl:flex flex-col justify-center leading-none">
              <span className="font-serif font-light text-[1.7rem] tracking-[0.32em] text-white">DELUX</span>
              <span className="text-[0.55rem] tracking-[0.58em] text-gold mt-1.5">CONCIERGE</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-5 2xl:gap-9" data-testid="desktop-nav">
            <NavLink to="/" data-testid="nav-home" className={navClass}>{t("Home")}</NavLink>
            <NavLink to="/about" data-testid="nav-about-us" className={navClass}>{t("About Us")}</NavLink>

            <div className="group relative">
              <NavLink to="/services" data-testid="nav-our-services" className={navClass}>
                <span className="inline-flex items-center gap-1.5">
                  {t("Our Services")}
                  <ChevronDown strokeWidth={1.5} size={11} className="transition-transform duration-300 group-hover:rotate-180" />
                </span>
              </NavLink>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-6 invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300">
                <div className="w-[36rem] bg-[#0A0A0A]/95 backdrop-blur-xl border border-white/10 px-8 py-6 grid grid-cols-3 gap-x-6 shadow-2xl">
                  {MENU_COLUMNS.map((col, ci) => (
                    <div key={ci} className="flex flex-col">
                      {col.map((sid) => {
                        const svc = SERVICES.find((x) => x.id === sid);
                        return (
                          <Link
                            key={sid}
                            to={`/services/${sid}`}
                            data-testid={`nav-service-${sid}`}
                            className="py-2.5 text-[0.8rem] font-light text-white/60 hover:text-gold transition-colors duration-300"
                          >
                            {t(svc.name)}
                          </Link>
                        );
                      })}
                      {ci === 2 && (
                        <Link
                          to="/membership"
                          data-testid="nav-service-memberships"
                          className="py-2.5 text-[0.8rem] font-light text-white/60 hover:text-gold transition-colors duration-300"
                        >
                          {t("Memberships")}
                        </Link>
                      )}
                    </div>
                  ))}
                  <Link
                    to="/services"
                    data-testid="nav-all-services"
                    className="col-span-3 mt-4 pt-4 border-t border-white/10 text-[0.62rem] uppercase tracking-[0.3em] text-gold hover:text-gold-bright transition-colors duration-300"
                  >
                    {t("View All Services")}
                  </Link>
                </div>
              </div>
            </div>

            <div className="group relative">
              <NavLink to="/membership" data-testid="nav-membership" className={navClass}>
                <span className="inline-flex items-center gap-1.5">
                  {t("Membership")}
                  <ChevronDown strokeWidth={1.5} size={11} className="transition-transform duration-300 group-hover:rotate-180" />
                </span>
              </NavLink>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-6 invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300">
                <div className="w-80 bg-[#0A0A0A]/95 backdrop-blur-xl border border-white/10 px-7 py-6 space-y-5 shadow-2xl">
                  <Link to="/membership#individual" data-testid="nav-membership-individual" className="block group/item">
                    <span className="font-serif text-lg font-light text-white/85 group-hover/item:text-gold transition-colors duration-300">{t("Individual Membership")}</span>
                    <span className="block text-[0.68rem] text-white/40 font-light mt-1 tracking-wide">{t("A personal advisor, around the clock")}</span>
                  </Link>
                  <Link to="/membership#corporate" data-testid="nav-membership-corporate" className="block group/item">
                    <span className="font-serif text-lg font-light text-white/85 group-hover/item:text-gold transition-colors duration-300">{t("Corporate Membership")}</span>
                    <span className="block text-[0.68rem] text-white/40 font-light mt-1 tracking-wide">{t("Concierge for your company & people")}</span>
                  </Link>
                </div>
              </div>
            </div>

            <NavLink to="/blog" data-testid="nav-blog" className={navClass}>{t("Blog")}</NavLink>
            <NavLink to="/contact" data-testid="nav-contact-us" className={navClass}>{t("Contact Us")}</NavLink>
          </nav>

          <div className="hidden xl:flex items-center gap-4 2xl:gap-7 flex-shrink-0">
            <div className="group relative">
              <button
                data-testid="language-selector-button"
                className="flex items-center gap-1.5 whitespace-nowrap text-[0.62rem] 2xl:text-[0.68rem] uppercase tracking-[0.22em] 2xl:tracking-[0.28em] text-white/70 hover:text-gold transition-colors duration-300"
              >
                {lang}
                <ChevronDown strokeWidth={1.5} size={11} className="transition-transform duration-300 group-hover:rotate-180" />
              </button>
              <div className="absolute right-0 top-full pt-4 invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300">
                <div className="w-24 bg-[#0A0A0A]/95 backdrop-blur-xl border border-white/10 py-2 shadow-2xl">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l}
                      data-testid={`language-option-${l.toLowerCase()}`}
                      onClick={() => setLang(l)}
                      className={`block w-full text-left px-4 py-2 text-[0.65rem] tracking-[0.25em] transition-colors duration-300 ${
                        lang === l ? "text-gold" : "text-white/60 hover:text-gold"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {user ? (
              <Link
                to="/portal"
                data-testid="header-portal-link"
                className="relative whitespace-nowrap text-[0.62rem] 2xl:text-[0.68rem] uppercase tracking-[0.22em] 2xl:tracking-[0.28em] text-gold hover:text-gold-bright transition-colors duration-300"
              >
                {t("My Portal")}
                {updatesAvailable && (
                  <span data-testid="header-portal-update-dot" className="absolute -top-1.5 -right-3 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                )}
              </Link>
            ) : (
              <button
                data-testid="header-signin-button"
                onClick={openLogin}
                className="whitespace-nowrap text-[0.62rem] 2xl:text-[0.68rem] uppercase tracking-[0.22em] 2xl:tracking-[0.28em] text-white/70 hover:text-gold transition-colors duration-300"
              >
                {t("Member Login")}
              </button>
            )}
            <button
              data-testid="header-enquire-button"
              onClick={() => langNavigate("/contact")}
              className="whitespace-nowrap border border-gold/70 text-gold text-[0.62rem] 2xl:text-[0.68rem] uppercase tracking-[0.22em] 2xl:tracking-[0.28em] px-5 2xl:px-7 py-3 hover:bg-gold hover:text-black transition-colors duration-500"
            >
              {t("Enquire")}
            </button>
          </div>

          <button
            data-testid="mobile-menu-button"
            className="xl:hidden text-white"
            onClick={() => setOpen(true)}
            aria-label={t("Open menu")}
          >
            <Menu strokeWidth={1.25} size={28} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-xl flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex items-center justify-between px-6 h-24">
              <img src="/logo-gold.png" alt="Delux Concierge" className="h-12 w-auto" />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label={t("Close menu")}>
                <X strokeWidth={1.25} size={30} className="text-white" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setOpen(false)}
                    className="font-serif text-4xl font-light text-white/80 hover:text-gold transition-colors duration-300 py-3 block"
                  >
                    {t(l.label)}
                  </NavLink>
                </motion.div>
              ))}
              <motion.button
                data-testid="mobile-signin-button"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                onClick={() => {
                  setOpen(false);
                  if (user) {
                    langNavigate("/portal");
                  } else {
                    openLogin();
                  }
                }}
                className="relative mt-10 self-start border border-gold/70 text-gold text-xs uppercase tracking-[0.3em] px-8 py-4"
              >
                {user ? t("My Portal") : t("Member Login")}
                {user && updatesAvailable && (
                  <span data-testid="mobile-portal-update-dot" className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                )}
              </motion.button>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-10 flex flex-wrap gap-2.5"
                data-testid="mobile-language-selector"
              >
                {LANGUAGES.map((l) => (
                  <button
                    key={l}
                    data-testid={`mobile-language-${l.toLowerCase()}`}
                    onClick={() => setLang(l)}
                    className={`text-[0.62rem] tracking-[0.25em] px-3 py-1.5 border transition-colors duration-300 ${
                      lang === l ? "border-gold text-gold" : "border-white/15 text-white/50 hover:border-white/40"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
